import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, CheckIcon, DatabaseIcon, EyeIcon, EyeOffIcon, GlobeIcon, LoaderIcon, LockIcon, RefreshCwIcon, ServerIcon, ShieldCheckIcon, UserIcon, XIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { checkAdminSlug } from '../../data/reservedRoutes';
import { INSTALLER_IS_CONNECTED, checkSystem, forgetToken, install, status as readStatus, testDatabase } from '../../services/installService';
import type { AdminAccountDraft, DatabaseSettings, DatabaseTestResult, InstallResult, InstallStatus, PlatformIdentity, SeoSettings, SystemCheck } from '../../types/install';

/**
 * The Campuzon installer.
 *
 * This screen collects configuration and reports what the server did with it.
 * It performs no part of the installation itself — every check, every test and
 * every write happens on the server, and this page shows only what the server
 * returned. Where there is nothing to talk to, it says so rather than showing
 * a wizard that would appear to work.
 */

type StepId = 'system' | 'database' | 'platform' | 'admin-url' | 'administrator' | 'seo' | 'security' | 'review' | 'installing' | 'complete';
const STEPS: Array<{
  id: StepId;
  label: string;
}> = [{
  id: 'system',
  label: 'System check'
}, {
  id: 'database',
  label: 'Database'
}, {
  id: 'platform',
  label: 'Platform'
}, {
  id: 'admin-url',
  label: 'Admin URL'
}, {
  id: 'administrator',
  label: 'Administrator'
}, {
  id: 'seo',
  label: 'Website'
}, {
  id: 'security',
  label: 'Security'
}, {
  id: 'review',
  label: 'Review'
}];

/* ------------------------------------------------------------------ helpers */

function Field({
  label,
  hint,
  children
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-8958" data-mp="15jvqyf86pzgo">
      <span className="block text-body-sm font-semibold text-ink" data-id="element-8959" data-mp="10mnaqk6lk5i2">{label}</span>
      {hint && <span className="mt-0.5 block text-caption text-ink-subtle" data-id="element-8960" data-mp="2v3n9u00fh5xd">{hint}</span>}
      <span className="mt-1.5 block" data-id="element-8961" data-mp="2v1iveo4wkoiu">{children}</span>
    </label>;
}
const inputClass = 'w-full min-h-[48px] rounded-lg border border-line bg-white px-3 text-body text-ink placeholder:text-ink-subtle focus:border-brand focus:outline-none';
function Secret({
  value,
  onChange,
  placeholder
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const [shown, setShown] = useState(false);
  return <span className="relative block" data-id="element-8962" data-mp="3it7vwrrvojav">
      <input type={shown ? 'text' : 'password'} value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} autoComplete="new-password" className={`${inputClass} pr-12`} data-id="element-8963" data-mp="3is6gyc7qrcnv" />
      <button type="button" onClick={() => setShown(current => !current)} aria-label={shown ? 'Hide' : 'Show'} className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-ink-muted hover:text-ink" data-id="element-8964" data-mp="1sg8woou5qada">
        {shown ? <EyeOffIcon className="h-5 w-5" data-id="element-8965" data-mp="ajoqmgxu54p2" /> : <EyeIcon className="h-5 w-5" data-id="element-8966" data-mp="ainhii0dqs9z" />}
      </button>
    </span>;
}
function passwordStrength(value: string): {
  score: number;
  note: string;
} {
  let score = 0;
  if (value.length >= 12) score += 1;
  if (value.length >= 16) score += 1;
  if (/[a-z]/.test(value) && /[A-Z]/.test(value)) score += 1;
  if (/\d/.test(value)) score += 1;
  if (/[^A-Za-z0-9]/.test(value)) score += 1;
  const notes = ['Too short to protect the whole platform.', 'Weak — this account can change everything here.', 'Acceptable, but a longer passphrase is stronger.', 'Good.', 'Strong.', 'Strong.'];
  return {
    score,
    note: notes[score]
  };
}
const masked = '••••••••••';

/* -------------------------------------------------------------------- page */

export function Install() {
  const [step, setStep] = useState<StepId>('system');
  const [state, setState] = useState<InstallStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [checks, setChecks] = useState<SystemCheck[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [database, setDatabase] = useState<DatabaseSettings>({
    engine: 'postgres',
    host: 'localhost',
    port: 5432,
    name: 'campuzon',
    user: '',
    password: '',
    ssl: 'prefer'
  });
  const [dbResult, setDbResult] = useState<DatabaseTestResult | null>(null);
  const [dbTesting, setDbTesting] = useState(false);
  const [dbError, setDbError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PlatformIdentity>({
    name: 'Campuzon',
    shortName: 'Campuzon',
    organization: 'Campuzon Technologies',
    domain: typeof window !== 'undefined' ? window.location.origin : '',
    supportEmail: '',
    timezone: 'Africa/Lagos',
    currency: 'NGN',
    country: 'NG',
    locale: 'en-NG'
  });
  const [adminSlug, setAdminSlug] = useState('platform-control');
  const slugVerdict = useMemo(() => checkAdminSlug(adminSlug), [adminSlug]);
  const [admin, setAdmin] = useState<AdminAccountDraft>({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [seo, setSeo] = useState<SeoSettings>({
    siteTitle: 'Campuzon',
    metaTitle: 'Campuzon — your campus, connected',
    metaDescription: 'Find housing and people you can live with, manage your academics, and reach opportunities around your campus.',
    siteUrl: typeof window !== 'undefined' ? window.location.origin : '',
    socialTitle: 'Campuzon',
    socialDescription: 'Housing, academics, community and opportunities around your campus.',
    socialImageUrl: '',
    faviconUrl: '',
    logoUrl: '',
    robots: 'index,follow'
  });
  const [security, setSecurity] = useState({
    sessionHours: 12,
    passwordMinLength: 12,
    requireMfaForAdmins: true,
    loginAttemptsBeforeThrottle: 5,
    apiRequestsPerMinute: 120,
    secureCookies: true,
    sameSite: 'lax' as const
  });
  const [result, setResult] = useState<InstallResult | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);

  /* ------------------------------------------------- server-side gate first */

  useEffect(() => {
    let alive = true;
    readStatus().then(value => {
      if (alive) setState(value);
    }).catch((failure: Error) => {
      if (alive) setStatusError(failure.message);
    }).finally(() => {
      if (alive) setLoadingStatus(false);
    });
    return () => {
      alive = false;
      forgetToken();
    };
  }, []);
  if (loadingStatus) {
    return <Frame data-id="element-8967" data-mp="2rumcnyxt855l">
        <div className="flex items-center gap-3 text-body text-ink-muted" data-id="element-8968" data-mp="2wrd057by2311">
          <LoaderIcon className="h-5 w-5 animate-spin" aria-hidden data-id="element-8969" data-mp="2wth2vly3wvzq" />
          Asking the server whether this deployment is installed…
        </div>
      </Frame>;
  }

  // The browser never decides this. If the server has not answered, the
  // installer does not open — an installer that runs on an assumption is a
  // way to overwrite a working platform.
  if (statusError) {
    return <Frame data-id="element-8970" data-mp="2i6qszw6bhv2j">
        <Notice tone="danger" title="The installer cannot run" data-id="element-8971" data-mp="2i70hffbpru64">
          <p data-id="element-8972" data-mp="2w2vyrgqgsx7e">{statusError}</p>
          <p className="mt-2" data-id="element-8973" data-mp="2w22nq3kh3ljg">
            {INSTALLER_IS_CONNECTED ? 'The API origin is configured but the installation service did not answer. Check that it is running and reachable.' : 'Set the API origin for this deployment first — INSTALLATION.md has the two ways to do it. Until then there is no server to install, and this page will not pretend otherwise.'}
          </p>
        </Notice>
      </Frame>;
  }
  if (state && state.state === 'INSTALLED') {
    return <Frame data-id="element-8974" data-mp="1vh0ulg3ef4d6">
        <Notice tone="ok" title="Installation already completed" data-id="element-8975" data-mp="1vh94cz485tk5">
          <p data-id="element-8976" data-mp="1vj3gpp1fkdcm">
            This deployment is installed, so the installer is closed. The server rejects{' '}
            <code data-id="element-8977" data-mp="20hwxbqc0kkh0">/api/install/*</code> as well — this is not a hidden page.
          </p>
        </Notice>
      </Frame>;
  }
  if (state && state.state === 'INSTALLING' && state.lockedBy) {
    return <Frame data-id="element-8978" data-mp="1cqj5dlhryqwk">
        <Notice tone="warn" title="Another installation is in progress" data-id="element-8979" data-mp="1cr19136ro7ei">
          <p data-id="element-8980" data-mp="1cooii1my0opu">
            An installation started elsewhere holds the lock. Two installations at once could create
            two administrators or run migrations twice, so this one will not start.
          </p>
        </Notice>
      </Frame>;
  }

  /* ------------------------------------------------------------- step logic */

  const criticalFailure = (checks ?? []).some(check => check.critical && check.status === 'fail');
  const stepIndex = STEPS.findIndex(entry => entry.id === step);
  const completed: Record<StepId, boolean> = {
    system: Boolean(checks) && !criticalFailure,
    database: Boolean(dbResult?.connected),
    platform: platform.name.trim().length > 1 && /.+@.+\..+/.test(platform.supportEmail),
    'admin-url': slugVerdict.ok,
    administrator: admin.fullName.trim().length > 1 && admin.username.trim().length > 2 && /.+@.+\..+/.test(admin.email) && admin.password.length >= security.passwordMinLength && admin.password === admin.confirmPassword,
    seo: seo.siteTitle.trim().length > 0 && seo.metaDescription.trim().length > 20,
    security: true,
    review: false,
    installing: false,
    complete: false
  };
  async function runChecks() {
    setChecking(true);
    setCheckError(null);
    try {
      const result = await checkSystem();
      setChecks(result.checks);
    } catch (failure) {
      setCheckError(failure instanceof Error ? failure.message : 'The system check failed.');
    } finally {
      setChecking(false);
    }
  }
  async function runDbTest() {
    setDbTesting(true);
    setDbError(null);
    setDbResult(null);
    try {
      setDbResult(await testDatabase(database));
    } catch (failure) {
      setDbError(failure instanceof Error ? failure.message : 'The database test failed.');
    } finally {
      setDbTesting(false);
    }
  }
  async function runInstall() {
    setStep('installing');
    setInstallError(null);
    try {
      const outcome = await install({
        database,
        platform,
        adminSlug,
        admin,
        seo,
        security,
        apiUrl: platform.domain
      });
      setResult(outcome);
      setStep(outcome.state === 'INSTALLED' ? 'complete' : 'installing');
      if (outcome.state === 'INSTALLED') forgetToken();
    } catch (failure) {
      setInstallError(failure instanceof Error ? failure.message : 'The installation failed.');
    }
  }

  /* ------------------------------------------------------------------ views */

  return <Frame data-id="element-8981" data-mp="tlp906mgknqo">
      {step !== 'complete' && step !== 'installing' && <nav aria-label="Installation steps" className="mb-6" data-id="element-8982" data-mp="11zu1fuvvfqt6">
          <ol className="flex flex-wrap gap-2" data-id="element-8983" data-mp="162qbuyb9cgm1">
            {STEPS.map((entry, index) => {
          const active = entry.id === step;
          const done = completed[entry.id];
          return <li key={entry.id} data-id="element-8984" data-mp="x0qv8745nejm" data-mpi={index}>
                  <button type="button" onClick={() => setStep(entry.id)} disabled={index > 0 && !checks} className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-body-sm disabled:opacity-50 ${active ? 'border-brand bg-brand text-white' : 'border-line bg-white text-ink-muted hover:text-ink'}`} data-id="element-8985" data-mp="x1sfn2729de0">
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full text-caption ${active ? 'bg-white/20' : done ? 'bg-ok-100 text-ok-800' : 'bg-canvas'}`} data-id="element-8986" data-mp="23nw2nvz5v8cz">
                      {done && !active ? <CheckIcon className="h-3 w-3" data-id="element-8987" data-mp="2dee2gmrm169v" /> : index + 1}
                    </span>
                    {entry.label}
                  </button>
                </li>;
        })}
          </ol>
        </nav>}

      {/* ------------------------------------------------------ system check */}
      {step === 'system' && <Section icon={<ServerIcon className="h-5 w-5" data-id="element-8989" data-mp="1umbk96zblhxi" />} title="System check" lede="The server checks its own runtime, drivers, permissions and files. Nothing here is checked in your browser, because your browser cannot see the server's disk." data-id="element-8988" data-mp="1umlej6reeb90">
          {!checks && !checking && <Button onClick={runChecks} icon={<RefreshCwIcon className="h-4 w-4" data-id="element-8991" data-mp="1gpey3iu5comy" />} data-id="element-8990" data-mp="1gm0ts51xa5cv">
              Run system check
            </Button>}
          {checking && <p className="flex items-center gap-2 text-body text-ink-muted" data-id="element-8992" data-mp="3ncdwctiky4j9">
              <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden data-id="element-8993" data-mp="3safe9b49rxt5" /> Checking the server…
            </p>}
          {checkError && <Notice tone="danger" title="The check could not run" data-id="element-8994" data-mp="7ei6v527awf">
              <p data-id="element-8995" data-mp="567wuqw317y6">{checkError}</p>
            </Notice>}
          {checks && <>
              <ul className="mt-2 space-y-2" data-id="element-8996" data-mp="34lw7j00vbt36">
                {checks.map(check => <li key={check.id} className="rounded-lg border border-line bg-white p-3" data-id="element-8997" data-mp="38m6jquf97fqh">
                    <div className="flex items-start gap-2" data-id="element-8998" data-mp="3djol9zqxysog">
                      <StatusMark status={check.status} data-id="element-8999" data-mp="3idjd5qgpadym" />
                      <div className="min-w-0" data-id="element-9000" data-mp="2kx3twzagugkw">
                        <p className="text-body font-semibold text-ink" data-id="element-9001" data-mp="2kwccqthm8le5">{check.label}</p>
                        <p className="text-body-sm text-ink-muted" data-id="element-9002" data-mp="2pv5fbqvd6rzj">{check.detail}</p>
                        <p className="mt-1 text-caption text-ink-subtle" data-id="element-9003" data-mp="wcolsl9gwi2w">{check.matters}</p>
                        {check.remedy && <p className="mt-1 text-body-sm text-ink" data-id="element-9004" data-mp="rfy3rse3rceu">
                            <span className="font-semibold" data-id="element-9005" data-mp="1561qekgw984t">To fix: </span>
                            {check.remedy}
                          </p>}
                      </div>
                    </div>
                  </li>)}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-9006" data-mp="1a5e7spozxn00">
                <Button variant="secondary" onClick={runChecks} data-id="element-9007" data-mp="1a6z4akuvfg0i">
                  Recheck
                </Button>
                <Button onClick={() => setStep('database')} disabled={criticalFailure} data-id="element-9008" data-mp="1o2b6tef9qarz">
                  {criticalFailure ? 'Fix the failures above to continue' : 'Continue'}
                </Button>
              </div>
            </>}
        </Section>}

      {/* ---------------------------------------------------------- database */}
      {step === 'database' && <Section icon={<DatabaseIcon className="h-5 w-5" data-id="element-9010" data-mp="3fru5dfa7jr53" />} title="Database" lede="PostgreSQL. The password is sent to the server once, over the connection you are on, and is never returned by any endpoint or shown again." data-id="element-9009" data-mp="3bsl9o4vlxj6w">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-9011" data-mp="36yh33mq97d6c">
            <Field label="Host" data-id="element-9012" data-mp="2s2hp5vggqita">
              <input className={inputClass} value={database.host} onChange={event => setDatabase({
            ...database,
            host: event.target.value
          })} data-id="element-9013" data-mp="2s1g9teseza5r" />
            </Field>
            <Field label="Port" data-id="element-9014" data-mp="2j3ntq8evbyhb">
              <input type="number" className={inputClass} value={database.port} onChange={event => setDatabase({
            ...database,
            port: Number(event.target.value) || 5432
          })} data-id="element-9015" data-mp="2o0wf0hvbcims" />
            </Field>
            <Field label="Database name" hint="Created for you if it does not exist and the account may create databases." data-id="element-9016" data-mp="jumkpdbcdjf5">
              <input className={inputClass} value={database.name} onChange={event => setDatabase({
            ...database,
            name: event.target.value
          })} data-id="element-9017" data-mp="fvdqk7ce3jj2" />
            </Field>
            <Field label="Username" data-id="element-9018" data-mp="13ivka8f7sroo">
              <input className={inputClass} value={database.user} onChange={event => setDatabase({
            ...database,
            user: event.target.value
          })} data-id="element-9019" data-mp="13htzhc8ecqtr" />
            </Field>
            <Field label="Password" data-id="element-9020" data-mp="tr7qi0v6qyk1">
              <Secret value={database.password} onChange={value => setDatabase({
            ...database,
            password: value
          })} data-id="element-9021" data-mp="ou8vzh7m0plb" />
            </Field>
            <Field label="SSL" data-id="element-9022" data-mp="1mb6bh50ib16y">
              <select className={inputClass} value={database.ssl} onChange={event => setDatabase({
            ...database,
            ssl: event.target.value as DatabaseSettings['ssl']
          })} data-id="element-9023" data-mp="1hid4eahkfgrp">
                <option value="require" data-id="element-9024" data-mp="1yryvzpaeywjq">Required</option>
                <option value="prefer" data-id="element-9025" data-mp="1yr5up3u84z8z">Preferred</option>
                <option value="disable" data-id="element-9026" data-mp="23qsddsstn7p6">Disabled</option>
              </select>
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-9027" data-mp="27tf75z91z2p0">
            <Button variant="secondary" onClick={runDbTest} loading={dbTesting} data-id="element-9028" data-mp="2crqheb39ff7r">
              Test database connection
            </Button>
            {dbResult?.connected && <span className="text-body-sm text-ok-800" data-id="element-9029" data-mp="2hkjlcwqwmjis">
                Connected{dbResult.serverVersion ? ` — ${dbResult.serverVersion}` : ''}
                {dbResult.created ? ' · database created' : ''}
              </span>}
          </div>

          {dbError && <Notice tone="danger" title="The connection failed" data-id="element-9030" data-mp="2virgr08alcru">
              <p data-id="element-9031" data-mp="3gsetgpxycjda">{dbError}</p>
            </Notice>}
          {dbResult && !dbResult.connected && <Notice tone="danger" title="Not connected" data-id="element-9032" data-mp="3cqjl51hlt5q0">
              <p data-id="element-9033" data-mp="3qrccjdclzkow">{dbResult.message}</p>
              {!dbResult.canCreateDatabase && <p className="mt-2" data-id="element-9034" data-mp="3qozznelpk0ip">
                  The database does not exist and this account may not create one. Create it on the
                  server, then test again:{' '}
                  <code data-id="element-9035" data-mp="2y0m2f12g1887">CREATE DATABASE {database.name || 'campuzon'};</code>
                </p>}
            </Notice>}

          <StepNav onBack={() => setStep('system')} onNext={() => setStep('platform')} nextDisabled={!dbResult?.connected} nextHint="Test the connection before continuing." data-id="element-9036" data-mp="37t01tn61u2r7" />
        </Section>}

      {/* ---------------------------------------------------------- platform */}
      {step === 'platform' && <Section icon={<GlobeIcon className="h-5 w-5" data-id="element-9038" data-mp="pujxle9ipssk" />} title="Platform identity" lede="What this deployment is called, and the defaults it runs on. All of it stays changeable afterwards without touching source." data-id="element-9037" data-mp="pv1zaqey8z7v">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-9039" data-mp="gvyftlbvehsu">
            <Field label="Platform name" data-id="element-9040" data-mp="kvpbo3g97o48">
              <input className={inputClass} value={platform.name} onChange={event => setPlatform({
            ...platform,
            name: event.target.value
          })} data-id="element-9041" data-mp="kwic6momdhdx" />
            </Field>
            <Field label="Short name" data-id="element-9042" data-mp="1dijteslos9e0">
              <input className={inputClass} value={platform.shortName} onChange={event => setPlatform({
            ...platform,
            shortName: event.target.value
          })} data-id="element-9043" data-mp="zlekmkul8wf2" />
            </Field>
            <Field label="Organisation" data-id="element-9044" data-mp="3k9j270hlel67">
              <input className={inputClass} value={platform.organization} onChange={event => setPlatform({
            ...platform,
            organization: event.target.value
          })} data-id="element-9045" data-mp="3pa6yo1olbu7t" />
            </Field>
            <Field label="Primary domain" data-id="element-9046" data-mp="8byn8zooefzv">
              <input className={inputClass} value={platform.domain} onChange={event => setPlatform({
            ...platform,
            domain: event.target.value
          })} data-id="element-9047" data-mp="8axa8po3njfi" />
            </Field>
            <Field label="Support email" data-id="element-9048" data-mp="3gsjnns38s0rd">
              <input type="email" className={inputClass} value={platform.supportEmail} onChange={event => setPlatform({
            ...platform,
            supportEmail: event.target.value
          })} data-id="element-9049" data-mp="3grs3ddf3hpge" />
            </Field>
            <Field label="Timezone" data-id="element-9050" data-mp="3bv1p8vmup3xm">
              <input className={inputClass} value={platform.timezone} onChange={event => setPlatform({
            ...platform,
            timezone: event.target.value
          })} data-id="element-9051" data-mp="3brvzrfab801d" />
            </Field>
            <Field label="Currency" data-id="element-9052" data-mp="2j78moa9k9ugj">
              <input className={inputClass} value={platform.currency} onChange={event => setPlatform({
            ...platform,
            currency: event.target.value
          })} data-id="element-9053" data-mp="2j8bf4mmj84yk" />
            </Field>
            <Field label="Country" data-id="element-9054" data-mp="2s5u3o0zdx1f7">
              <input className={inputClass} value={platform.country} onChange={event => setPlatform({
            ...platform,
            country: event.target.value
          })} data-id="element-9055" data-mp="2n8lswlgkfznz" />
            </Field>
          </div>
          <StepNav onBack={() => setStep('database')} onNext={() => setStep('admin-url')} nextDisabled={!completed.platform} nextHint="A name and a valid support email are required." data-id="element-9056" data-mp="2x71de5l38aoi" />
        </Section>}

      {/* --------------------------------------------------------- admin url */}
      {step === 'admin-url' && <Section icon={<LockIcon className="h-5 w-5" data-id="element-9058" data-mp="3fvoi2mzcqy1k" />} title="Administration URL" lede="Administration does not have to live at /admin. Choose where it lives on this deployment." data-id="element-9057" data-mp="3fzc9ngl8ladn">
          <Field label="Administration URL" hint="Lower-case letters, numbers and hyphens." data-id="element-9059" data-mp="2hjvpc52cs68t">
            <span className="flex items-center gap-1" data-id="element-9060" data-mp="2miyhiox8ihzd">
              <span className="text-body text-ink-subtle" data-id="element-9061" data-mp="2rc1gjcks6jmy">{platform.domain || 'https://…'}/</span>
              <input className={inputClass} value={adminSlug} onChange={event => setAdminSlug(event.target.value)} data-id="element-9062" data-mp="2wbnph9tktdpy" />
            </span>
          </Field>
          {!slugVerdict.ok && <p className="mt-2 text-body-sm text-danger-700" data-id="element-9063" data-mp="359ga8ti2hdkq">{slugVerdict.reason}</p>}
          <Notice tone="warn" title="This is obscurity, not authorisation" data-id="element-9064" data-mp="3a8jbs3yu91ny">
            <p data-id="element-9065" data-mp="1az2ro62ybghf">
              A private URL means administration is not sitting at a guessable address. It is not a
              substitute for anything: administrators still authenticate, still need the permission
              for what they are doing, and every action is still audited. Treat it as one layer.
            </p>
          </Notice>
          <StepNav onBack={() => setStep('platform')} onNext={() => setStep('administrator')} nextDisabled={!slugVerdict.ok} nextHint="Choose a URL that does not collide with the platform." data-id="element-9066" data-mp="yn0qepk2s3xp" />
        </Section>}

      {/* ----------------------------------------------------- administrator */}
      {step === 'administrator' && <Section icon={<UserIcon className="h-5 w-5" data-id="element-9068" data-mp="1hd0bz2gpj0wd" />} title="Administrator account" lede="The first account. It receives the super administrator role and every platform permission, including experience.design.manage." data-id="element-9067" data-mp="1hdry7moezm9z">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-9069" data-mp="5zxum56sa7b5">
            <Field label="Full name" data-id="element-9070" data-mp="1whz5c6gad01">
              <input className={inputClass} value={admin.fullName} onChange={event => setAdmin({
            ...admin,
            fullName: event.target.value
          })} data-id="element-9071" data-mp="1vgdyevq0a4l" />
            </Field>
            <Field label="Username" hint="Choose something other than “admin”." data-id="element-9072" data-mp="1um8xbchhqq0h">
              <input className={inputClass} value={admin.username} onChange={event => setAdmin({
            ...admin,
            username: event.target.value
          })} data-id="element-9073" data-mp="1fxcq1hn9dh0x" />
            </Field>
            <Field label="Email" hint="Used for recovery, so it must be an address you hold." data-id="element-9074" data-mp="2dja2axb1oq4f">
              <input type="email" className={inputClass} value={admin.email} onChange={event => setAdmin({
            ...admin,
            email: event.target.value
          })} data-id="element-9075" data-mp="2dgxl4md42jsf" />
            </Field>
            <Field label="Phone (optional)" data-id="element-9076" data-mp="o3aqt9jyeolm">
              <input className={inputClass} value={admin.phone} onChange={event => setAdmin({
            ...admin,
            phone: event.target.value
          })} data-id="element-9077" data-mp="o6q364m8tjjc" />
            </Field>
            <Field label="Password" hint={`At least ${security.passwordMinLength} characters.`} data-id="element-9078" data-mp="fnlecplurkzi">
              <Secret value={admin.password} onChange={value => setAdmin({
            ...admin,
            password: value
          })} data-id="element-9079" data-mp="ukc4e89mzkcg" />
            </Field>
            <Field label="Confirm password" data-id="element-9080" data-mp="pjg6qcfvoufh">
              <Secret value={admin.confirmPassword} onChange={value => setAdmin({
            ...admin,
            confirmPassword: value
          })} data-id="element-9081" data-mp="1v5axnp1hky2" />
            </Field>
          </div>
          {admin.password.length > 0 && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-9082" data-mp="bnujoat7gkpp">
              {passwordStrength(admin.password).note}
            </p>}
          {admin.confirmPassword.length > 0 && admin.password !== admin.confirmPassword && <p className="mt-1 text-body-sm text-danger-700" data-id="element-9083" data-mp="3ac9s3thiro20">The two passwords do not match.</p>}
          <p className="mt-3 text-caption text-ink-subtle" data-id="element-9084" data-mp="3vayxj21l6w7">
            The password is hashed with Argon2id on the server. It is never stored in readable form,
            never written to the installation log, and cannot be shown back to you afterwards.
          </p>
          <StepNav onBack={() => setStep('admin-url')} onNext={() => setStep('seo')} nextDisabled={!completed.administrator} nextHint="Complete the account details." data-id="element-9085" data-mp="hlwjrdb23qwq" />
        </Section>}

      {/* --------------------------------------------------------------- seo */}
      {step === 'seo' && <Section icon={<GlobeIcon className="h-5 w-5" data-id="element-9087" data-mp="3q3slzzzic9cp" />} title="Website and sharing" lede="Stored as configuration and applied at runtime, so changing the title later never needs a rebuild." data-id="element-9086" data-mp="3q04v78lg611o">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-9088" data-mp="15dv0ict9yeh9">
            <Field label="Website title" data-id="element-9089" data-mp="1a9a7cksv7a46">
              <input className={inputClass} value={seo.siteTitle} onChange={event => setSeo({
            ...seo,
            siteTitle: event.target.value
          })} data-id="element-9090" data-mp="1acehrzwxb23a" />
            </Field>
            <Field label="Meta title" data-id="element-9091" data-mp="2l2az6zrcblit">
              <input className={inputClass} value={seo.metaTitle} onChange={event => setSeo({
            ...seo,
            metaTitle: event.target.value
          })} data-id="element-9092" data-mp="2l1892u1udn3y" />
            </Field>
            <Field label="Site URL" data-id="element-9093" data-mp="2c01oa8s5tq4w">
              <input className={inputClass} value={seo.siteUrl} onChange={event => setSeo({
            ...seo,
            siteUrl: event.target.value
          })} data-id="element-9094" data-mp="272id5hqrdth6" />
            </Field>
            <Field label="Search engines" data-id="element-9095" data-mp="1x5x6krz3cab8">
              <select className={inputClass} value={seo.robots} onChange={event => setSeo({
            ...seo,
            robots: event.target.value as SeoSettings['robots']
          })} data-id="element-9096" data-mp="1x6ykd47hrawn">
                <option value="index,follow" data-id="element-9097" data-mp="30ekdgnylni7m">Allow indexing</option>
                <option value="noindex,nofollow" data-id="element-9098" data-mp="35ccgad0s8oql">Do not index (staging)</option>
              </select>
            </Field>
          </div>
          <div className="mt-4" data-id="element-9099" data-mp="2rkfqifml6qxd">
            <Field label="Meta description" hint="Shown in search results and link previews." data-id="element-9100" data-mp="2rj4lydlbhf57">
              <textarea rows={3} className={`${inputClass} py-2`} value={seo.metaDescription} onChange={event => setSeo({
            ...seo,
            metaDescription: event.target.value
          })} data-id="element-9101" data-mp="2vkpw3r6gvfbp" />
            </Field>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-9102" data-mp="28nxtpjsvs0u9">
            <Field label="Favicon URL" data-id="element-9103" data-mp="2cn6j6cgw1ck0">
              <input className={inputClass} value={seo.faviconUrl} onChange={event => setSeo({
            ...seo,
            faviconUrl: event.target.value
          })} data-id="element-9104" data-mp="2co889kuqnzkq" />
            </Field>
            <Field label="Sharing image URL" data-id="element-9105" data-mp="au8wkydpfg7i">
              <input className={inputClass} value={seo.socialImageUrl} onChange={event => setSeo({
            ...seo,
            socialImageUrl: event.target.value
          })} data-id="element-9106" data-mp="1vfnq6639cy8b" />
            </Field>
          </div>
          <StepNav onBack={() => setStep('administrator')} onNext={() => setStep('security')} nextDisabled={!completed.seo} nextHint="A title and a description of some substance are required." data-id="element-9107" data-mp="256a1ho3xz2l7" />
        </Section>}

      {/* ---------------------------------------------------------- security */}
      {step === 'security' && <Section icon={<ShieldCheckIcon className="h-5 w-5" data-id="element-9109" data-mp="2j4gephhjw3sw" />} title="Security" lede="Secure defaults, and secrets you never have to invent." data-id="element-9108" data-mp="2o1744vf8wbqz">
          <Notice tone="ok" title="Secrets are generated on the server" data-id="element-9110" data-mp="tpioer16w55j">
            <p data-id="element-9111" data-mp="or8raavwmuha">
              The session secret and encryption key are generated with a cryptographic random source
              during installation, stored server-side, and never sent to a browser — not here, not
              on the review screen, and not by any endpoint afterwards. You will not be shown them,
              because anything shown can be captured.
            </p>
          </Notice>
          <div className="mt-4 grid gap-4 md:grid-cols-2" data-id="element-9112" data-mp="3hi2n4gavyoce">
            <Field label="Session length (hours)" data-id="element-9113" data-mp="3cjj0sesdraji">
              <input type="number" className={inputClass} value={security.sessionHours} onChange={event => setSecurity({
            ...security,
            sessionHours: Number(event.target.value) || 12
          })} data-id="element-9114" data-mp="3clc6nrv65qqg" />
            </Field>
            <Field label="Minimum password length" data-id="element-9115" data-mp="2ym4913enmqod">
              <input type="number" className={inputClass} value={security.passwordMinLength} onChange={event => setSecurity({
            ...security,
            passwordMinLength: Number(event.target.value) || 12
          })} data-id="element-9116" data-mp="11qojahc7rj3i" />
            </Field>
            <Field label="Failed sign-ins before throttling" data-id="element-9117" data-mp="1e1p93sa5w74z">
              <input type="number" className={inputClass} value={security.loginAttemptsBeforeThrottle} onChange={event => setSecurity({
            ...security,
            loginAttemptsBeforeThrottle: Number(event.target.value) || 5
          })} data-id="element-9118" data-mp="1e4bizykiyyqd" />
            </Field>
            <Field label="API requests per minute" data-id="element-9119" data-mp="10akitgsoor7b">
              <input type="number" className={inputClass} value={security.apiRequestsPerMinute} onChange={event => setSecurity({
            ...security,
            apiRequestsPerMinute: Number(event.target.value) || 120
          })} data-id="element-9120" data-mp="10c5cz5b3684n" />
            </Field>
          </div>
          <label className="mt-4 flex items-center gap-2" data-id="element-9121" data-mp="6o544ereku32">
            <input type="checkbox" className="h-5 w-5" checked={security.requireMfaForAdmins} onChange={event => setSecurity({
          ...security,
          requireMfaForAdmins: event.target.checked
        })} data-id="element-9122" data-mp="kofxbog6qkre" />
            <span className="text-body text-ink" data-id="element-9123" data-mp="3k3t9yspv2n32">Require multi-factor for administrators</span>
          </label>
          <StepNav onBack={() => setStep('seo')} onNext={() => setStep('review')} data-id="element-9124" data-mp="1pk0e5cxzs9v" />
        </Section>}

      {/* ------------------------------------------------------------ review */}
      {step === 'review' && <Section icon={<CheckIcon className="h-5 w-5" data-id="element-9126" data-mp="35cam68qrfo95" />} title="Review" lede="Everything the server will be asked to do. Secrets are masked here because there is no reason to render them." data-id="element-9125" data-mp="35cu5ntw26kpa">
          <dl className="divide-y divide-line rounded-lg border border-line bg-white" data-id="element-9127" data-mp="2r70kqsnb94fm">
            {[['Platform', platform.name], ['Organisation', platform.organization], ['Domain', platform.domain], ['Administration URL', `${platform.domain || ''}/${adminSlug}`], ['Database', `PostgreSQL · ${database.host}:${database.port} · ${database.name}`], ['Database user', database.user], ['Database password', masked], ['Administrator', `${admin.fullName} (@${admin.username})`], ['Administrator email', admin.email], ['Administrator password', masked], ['Session secret', 'Generated on the server · never displayed'], ['Encryption key', 'Generated on the server · never displayed'], ['Website title', seo.siteTitle], ['Meta description', seo.metaDescription], ['Indexing', seo.robots === 'index,follow' ? 'Allowed' : 'Blocked'], ['Timezone / currency', `${platform.timezone} · ${platform.currency}`]].map(([label, value]) => <div key={label} className="grid gap-1 p-3 sm:grid-cols-[220px_1fr]" data-id="element-9128" data-mp="2037mmj91po2e">
                <dt className="text-body-sm font-semibold text-ink" data-id="element-9129" data-mp="1v4e94qtrdx28">{label}</dt>
                <dd className="min-w-0 break-words text-body-sm text-ink-muted" data-id="element-9130" data-mp="2srn0v3kcs2co">{value}</dd>
              </div>)}
          </dl>
          <p className="mt-3 text-caption text-ink-subtle" data-id="element-9131" data-mp="2nsjv1gwjts3n">
            Design variant configuration for the homepage, housing and discover surfaces is created
            at Design 1 — the safe design — and is changeable afterwards from administration.
          </p>
          {installError && <Notice tone="danger" title="Installation failed" data-id="element-9132" data-mp="17fbttpmlkgaw">
              <p data-id="element-9133" data-mp="12eef5n0sq4e5">{installError}</p>
            </Notice>}
          <StepNav onBack={() => setStep('security')} onNext={runInstall} nextLabel="Install Campuzon" data-id="element-9134" data-mp="12h0krh44m9tq" />
        </Section>}

      {/* -------------------------------------------------------- installing */}
      {(step === 'installing' || step === 'complete') && <Section icon={<ServerIcon className="h-5 w-5" data-id="element-9136" data-mp="3nydvsalj62w9" />} title={step === 'complete' ? 'Installation complete' : 'Installing Campuzon'} lede={step === 'complete' ? 'The platform is configured and the installer is closed.' : 'Each line below is a step the server reported. Nothing here is a simulated progress bar.'} data-id="element-9135" data-mp="3nyw1dxu31tgu">
          <ol className="space-y-2" data-id="element-9137" data-mp="aj7t7d85jodo">
            {(result?.steps ?? []).map(entry => <li key={entry.id} className="flex items-start gap-2" data-id="element-9138" data-mp="fjm2szpej6dt">
                <StatusMark status={entry.status === 'done' ? 'pass' : entry.status === 'failed' ? 'fail' : 'warn'} data-id="element-9139" data-mp="2cdp8jhe6ho8u" />
                <div data-id="element-9140" data-mp="29aatmqxk2nyz">
                  <p className="text-body text-ink" data-id="element-9141" data-mp="29bkqxdwl3s4k">{entry.label}</p>
                  {entry.detail && <p className="text-body-sm text-ink-muted" data-id="element-9142" data-mp="25asi2lrmjp8t">{entry.detail}</p>}
                </div>
              </li>)}
            {!result && <li className="flex items-center gap-2 text-body text-ink-muted" data-id="element-9143" data-mp="2eaf9ceqn3eo5">
                <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden data-id="element-9144" data-mp="2eci75ky1ac3f" /> Working…
              </li>}
          </ol>

          {result?.failure && <Notice tone="danger" title="Installation did not complete" data-id="element-9145" data-mp="2o7jtya4u722u">
              <p data-id="element-9146" data-mp="31zzu936csm02">{result.failure}</p>
              <p className="mt-2" data-id="element-9147" data-mp="31yyg2pu1jje4">
                Incomplete changes were rolled back and the failure was recorded, so the database is
                not left half-configured. Correct the cause and start again.
              </p>
            </Notice>}

          {step === 'complete' && result && <>
              <Notice tone="ok" title={`${platform.name} has been installed`} data-id="element-9148" data-mp="3qkmq6fsmc2a5">
                <p data-id="element-9149" data-mp="3qiiaz1p9rguk">
                  The installer is now disabled. <code data-id="element-9150" data-mp="3umhrhkajufnx">/install</code> and{' '}
                  <code data-id="element-9151" data-mp="3uk3uv7080ve9">/api/install/*</code> are rejected by the server, not merely hidden.
                </p>
              </Notice>
              <div className="mt-4 flex flex-wrap gap-2" data-id="element-9152" data-mp="3ll9ur6nolmvy">
                <Button onClick={() => window.location.assign(result.siteUrl ?? '/')} data-id="element-9153" data-mp="3ljoztfxgg1xk">
                  Open platform
                </Button>
                <Button variant="secondary" onClick={() => window.location.assign(result.adminUrl ?? `/${adminSlug}`)} data-id="element-9154" data-mp="3bnvo1yrk8cgu">
                  Open administration
                </Button>
              </div>
              <p className="mt-3 text-caption text-ink-subtle" data-id="element-9155" data-mp="32vaacx2scvyv">
                Your administration URL is <code data-id="element-9156" data-mp="2nyjgf3bvscgn">/{adminSlug}</code>. Save it. If it is ever lost,
                recover it on the server with <code data-id="element-9157" data-mp="2sw2spxp0qf5y">npm run admin:reset-route</code> — there is no
                public recovery, deliberately.
              </p>
            </>}
        </Section>}
    </Frame>;
}

/* ------------------------------------------------------------ presentation */

function Frame({
  children
}: {
  children: React.ReactNode;
}) {
  return <main className="min-h-screen bg-canvas px-4 py-8 sm:px-6 lg:py-12" data-id="element-9158" data-mp="2ufmdnohpgoha">
      <div className="mx-auto w-full max-w-3xl" data-id="element-9159" data-mp="2pfzw52uugm2o">
        <header className="mb-6" data-id="element-9160" data-mp="2pgt6edt0htpk">
          <div className="flex items-center gap-2" data-id="element-9161" data-mp="2phujsoxi2sag">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand text-white" data-id="element-9162" data-mp="2ko8fbk1hpupq">
              <ServerIcon className="h-5 w-5" aria-hidden data-id="element-9163" data-mp="2gor6qhqrohgc" />
            </span>
            <span className="text-h4 font-semibold text-ink" data-id="element-9164" data-mp="2bp4tw9eup382">Campuzon installation</span>
          </div>
          <p className="mt-2 max-w-[70ch] text-body text-ink-muted" data-id="element-9165" data-mp="26pryh09wyntv">
            Configure this deployment. Nothing here requires editing source code, and nothing is
            written until the final step.
          </p>
        </header>
        {children}
      </div>
    </main>;
}
function Section({
  icon,
  title,
  lede,
  children
}: {
  icon: React.ReactNode;
  title: string;
  lede: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-line bg-white p-4 sm:p-6" data-id="element-9166" data-mp="21lg5cenxqxoq">
      <div className="flex items-center gap-2 text-brand" data-id="element-9167" data-mp="26igbsqop62gd">{icon}</div>
      <h1 className="mt-2 text-h3 font-semibold text-ink" data-id="element-9168" data-mp="18witoue8bz4y">{title}</h1>
      <p className="mt-1 max-w-[70ch] text-body text-ink-muted" data-id="element-9169" data-mp="18u67u65cp4mm">{lede}</p>
      <div className="mt-5" data-id="element-9170" data-mp="1dvdbpu4dhjo0">{children}</div>
    </section>;
}
function StatusMark({
  status
}: {
  status: SystemCheck['status'];
}) {
  if (status === 'pass') {
    return <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ok-100 text-ok-800" data-id="element-9171" data-mp="1nlotokkw5tdw">
        <CheckIcon className="h-3.5 w-3.5" aria-label="Passed" data-id="element-9172" data-mp="q4zs8rzust51" />
      </span>;
  }
  if (status === 'fail') {
    return <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-danger-100 text-danger-700" data-id="element-9173" data-mp="v29ho7n8t4sv">
        <XIcon className="h-3.5 w-3.5" aria-label="Failed" data-id="element-9174" data-mp="20ew5hec6q8rh" />
      </span>;
  }
  return <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warn-100 text-warn-800" data-id="element-9175" data-mp="24h97psrq3oji">
      <AlertTriangleIcon className="h-3.5 w-3.5" aria-label="Warning" data-id="element-9176" data-mp="297zglfzcy9iy" />
    </span>;
}
function Notice({
  tone,
  title,
  children
}: {
  tone: 'ok' | 'warn' | 'danger';
  title: string;
  children: React.ReactNode;
}) {
  const tones = {
    ok: 'border-ok-200 bg-ok-50 text-ok-800',
    warn: 'border-warn-200 bg-warn-50 text-warn-800',
    danger: 'border-danger-200 bg-danger-50 text-danger-700'
  };
  return <div className={`mt-4 rounded-lg border p-3 ${tones[tone]}`} data-id="element-9177" data-mp="2s5aeox996wwt">
      <p className="text-body font-semibold" data-id="element-9178" data-mp="trh11hxpv7ix">{title}</p>
      <div className="mt-1 text-body-sm" data-id="element-9179" data-mp="tt1xxe7i72jy">{children}</div>
    </div>;
}
function StepNav({
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled,
  nextHint
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextHint?: string;
}) {
  return <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-line pt-4" data-id="element-9180" data-mp="lh0591p1p6zd">
      <Button variant="secondary" onClick={onBack} data-id="element-9181" data-mp="gjrliwo90uw0">
        Back
      </Button>
      <Button onClick={onNext} disabled={nextDisabled} data-id="element-9182" data-mp="ciobwrwsnv38">
        {nextLabel}
      </Button>
      {nextDisabled && nextHint && <span className="text-caption text-ink-subtle" data-id="element-9183" data-mp="197dteh4ieloo">{nextHint}</span>}
    </div>;
}